import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { Loader2 } from "lucide-react";

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;
  return children;
}

export function RequireRole({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;
  if (!roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

/**
 * Analyst-Mode shell guard.
 *
 * Grants access if the user is:
 *   - admin / analyst role (staff or paid analyst seat), OR
 *   - an active PREMIUM / ENTERPRISE subscriber.
 *
 * Standard subscribers (and any free authenticated user trying to deep-link
 * to /analyst/*) are redirected to the Pricing page with conversion intent
 * tagged so the funnel is traceable.
 */
const ANALYST_TIERS = new Set(["PREMIUM", "ENTERPRISE"]);
export function RequireAnalystAccess({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { tier, active, loading: subLoading } = useSubscription();
  const location = useLocation();
  if (authLoading || subLoading) return <FullPageLoader />;
  if (!user) return <Navigate to="/signin" state={{ from: location }} replace />;
  const isPrivilegedRole = user.role === "admin" || user.role === "analyst";
  const hasAnalystSub = active && ANALYST_TIERS.has(tier);
  if (!isPrivilegedRole && !hasAnalystSub) {
    return (
      <Navigate
        to="/pricing?intent=upgrade&from=analyst_direct&plan=PREMIUM"
        replace
        state={{ from: location }}
      />
    );
  }
  return children;
}

function FullPageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
      <Loader2 className="h-6 w-6 animate-spin text-[#061426]" />
    </div>
  );
}
